import type { FC, ReactNode } from 'react'
import type { Faq } from '@/types'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { FaqList } from '@/components/generals/faq-list'
import { StatGrid } from '@/components/generals/stat-grid'
import { GuideLinks } from '@/components/generals/guide-links'
import { ClosingCta } from '@/components/generals/closing-cta'
import { ProductHero } from '@/components/generals/product-hero'
import { FeatureChapters } from '@/components/generals/feature-chapters'
import { SentralHeroVisual } from '@/components/landing/sentral/hero-visual'
import { ApprovalGap } from '@/components/landing/sentral/approval-gap'
import { PHOTO, UI } from '@/constants/media'
import { CONTACT } from '@/constants/site'

const BOOK = { label: 'Book uforpliktende samtale', href: '/bli-oppringt?tema=sentral-godkjenning' }

const FAQ: Faq[] = [
    {
        question: 'Hva er sentral godkjenning, og trenger bedriften min det?',
        answer: [
            "Sentral godkjenning er en frivillig kvalitetsordning fra Direktoratet for byggkvalitet (DiBK) som dokumenterer at bedriften har kompetansen som kreves. Mange oppdragsgivere foretrekker godkjente bedrifter, og det kan gi deg flere og større jobber."
        ],
        link: { label: 'Les guiden til sentral godkjenning', href: '/blogg/sentral-godkjenning-guide-sjekkliste' }
    },
    {
        question: 'Hvordan hjelper Svenn meg gjennom søknaden?',
        answer: [
            "Vi samler dokumentene, skriver referanseprosjektene og fyller ut søknaden, som regel innen én virkedag. Tjenesten har søknadsgaranti med 3 søknader inkludert, og så langt er alle kundene våre godkjent på første forsøk."
        ]
    },
    {
        question: 'Dekker KS-systemet kravene til sentral godkjenning?',
        answer: [
            "Ja. KS-systemet er et tilvalg som koster ekstra, og dekker kravene fra Arbeidstilsynet og DiBK. Får du tilsyn, hjelper vi deg gjennom det."
        ],
        link: { label: 'Se Svenn Kvalitetssikring', href: '/kvalitetssikring-app-for-handverkere' }
    },
    {
        question: 'Hva skjer etter at jeg er blitt godkjent?',
        answer: [
            "Godkjenningen må vedlikeholdes. Årlig revisjon av rutinene er inkludert i abonnementet, så du holder deg godkjent uten ekstra kostnad."
        ]
    }
]

const SentralGodkjenning: FC = (): ReactNode => {
    return (
        <Layout header='dark'>
            <Seo
                title='Sentral godkjenning'
                description="Sentral godkjenning uten papirarbeidet. Vi tar oss av søknaden, som regel innen én virkedag, med søknadsgaranti. 600+ håndverkere hjulpet. Fra 5 800 kr."
            />

            <ProductHero
                tone='night'
                eyebrow='Sentral godkjenning · 600+ håndverkere sentralt godkjent'
                titleA='Sentralt godkjent.'
                titleB='Uten papirarbeidet.'
                text='Vi tar oss av papirene og sender som regel inn søknaden innen én virkedag. Fra 5 800 kr, for alle tiltaksklasser.'
                visual={<SentralHeroVisual />}
                primary={BOOK}
                secondary={{ label: 'Bli oppringt', href: '/bli-oppringt?tema=sentral-godkjenning' }}
                points={['Alle tiltaksklasser', 'Søknad innen 1 virkedag', 'KS-system som tilvalg']}
                quote={{
                    quote: 'Det ga jeg opp, og fikk bistand fra Svenn.',
                    name: 'Georg Hågensen',
                    company: 'Hh Håndverk AS'
                }}
            />

            <ApprovalGap />

            <StatGrid
                index='02'
                tone='night'
                eyebrow='Tallene'
                title='Tallene snakker for seg selv.'
                intro='Vi har gjort jobben mange ganger før, og vet nøyaktig hva søknaden trenger.'
                stats={[
                    {
                        label: 'Hjulpet',
                        value: '600+',
                        text: 'håndverkere hjulpet til sentral godkjenning.'
                    },
                    {
                        label: 'Første forsøk',
                        value: '100',
                        unit: '%',
                        text: 'av kundene våre er godkjent på første forsøk.'
                    },
                    {
                        label: 'Tid til søknad',
                        value: '1',
                        unit: 'virkedag',
                        text: 'er vanlig tid fra første samtale til innsendt søknad.'
                    }
                ]}
            />

            <FeatureChapters
                index='03'
                tone='mist'
                eyebrow='Hele prosessen'
                title='Vi tar deg gjennom hele prosessen.'
                intro='Fra søknaden til rutinene som holder deg godkjent år etter år.'
                chapters={[
                    {
                        label: 'Søknad',
                        title: 'Eksperthjelp med selve søknaden.',
                        text: "Ekspertene våre samler dokumentene og skriver referanseprosjektene for alle tiltaksklasser. Med søknadsgaranti.",
                        points: [
                            { strong: 'Alle tiltaksklasser' },
                            { before: 'Vi skriver', strong: 'referanseprosjektene' },
                            { strong: 'Søknadsgaranti', after: 'med 3 søknader inkludert' }
                        ],
                        image: UI.documentationFiles,
                        imageAlt: 'Dokumentasjon samlet i Svenn'
                    },
                    {
                        label: 'HMS og KS',
                        title: 'HMS og KS som dekker kravene.',
                        text: "Vi setter opp HMS- og KS-rutiner som følger kravene fra Arbeidstilsynet og DiBK.",
                        points: [
                            { before: 'Rutiner etter kravene fra', strong: 'Arbeidstilsynet og DiBK' },
                            { strong: 'Satt opp for deg', after: 'av ekspertene våre' }
                        ],
                        image: PHOTO.hmsInspection,
                        imageAlt: 'Kontrollør og håndverker går gjennom dokumentasjon på byggeplassen'
                    },
                    {
                        label: 'Sjekklister',
                        title: 'Sjekklister de ansatte faktisk bruker.',
                        text: "Sjekklisteappen har avvik, bilder og egne skjemaer, og finnes på flere språk, blant annet engelsk og polsk.",
                        points: [
                            { strong: 'Avvik og bilder', after: 'rett fra sjekklisten' },
                            { strong: 'Egne skjemaer', after: 'tilpasset bedriften' },
                            { before: 'Flere språk, blant annet', strong: 'engelsk og polsk' }
                        ],
                        image: UI.checklistsPhone,
                        imageAlt: 'Sjekklister i Svenn-appen'
                    },
                    {
                        label: 'Årlig revisjon',
                        title: 'Årlig revisjon, uten ekstra kostnad.',
                        text: "Rutinene gjennomgås hvert år som en del av abonnementet, så godkjenningen holdes ved like.",
                        points: [
                            { strong: 'Gjennomgang hvert år', after: 'inkludert i abonnementet' }
                        ],
                        image: UI.checklistDocument,
                        imageAlt: 'Utfylt sjekklistedokument fra Svenn'
                    }
                ]}
            />

            <FaqList items={FAQ} index='04' />

            <ClosingCta
                tone='signal'
                eyebrow='Neste steg'
                lines={['Klar til å bli', 'sentralt godkjent?']}
                text='Book en gratis og uforpliktende samtale, så forteller vi hva som skal til for din bedrift.'
                primary={BOOK}
                secondary={{ label: `Ring ${CONTACT.phone}`, href: CONTACT.phoneHref }}
                note='Sentral godkjenning fra 5 800 kr. Pris eks. mva.'
            />

            <GuideLinks
                index='05'
                title='Lær mer om sentral godkjenning'
                intro='Guider fra bloggen og Håndverkerordboken.'
                guides={[
                    { kind: 'Guide og gratis sjekkliste', title: 'Sentral godkjenning i 2026: komplett guide', href: '/blogg/sentral-godkjenning-guide-sjekkliste' },
                    { kind: 'Analyse', title: 'Hvor viktig er det med sentral godkjenning?', href: '/blogg/hvor-viktig-er-det-med-sentral-godkjenning' },
                    { kind: 'Håndverkerordboken', title: 'Hva er en tiltaksklasse?', href: '/haandverkerordboken/tiltaksklasse' }
                ]}
            />
        </Layout>
    )
}

export default SentralGodkjenning
